import { apiRequest } from 'js-api-client';

/**
 * Master cities catalog service.
 *
 * Fetches the transporter's master city catalog from the purchase API
 * (`GET /api/v2/master_cities`), which proxies saas-integrations
 * `GET /master-cities/{provider}`. Used to populate the city selector for
 * providers that require a provider city id (e.g. Flota Ospina).
 *
 * The catalog is large (the full DIVIPOLA list) and shared by several callers
 * (per-passenger and purchaser city selectors), each of which mounts its own
 * `useMasterCities` hook. To avoid hammering the endpoint with duplicate
 * requests, the in-flight/resolved promise is memoized per purchase token so all
 * callers reuse a single request. Failures are not cached, so a later mount can
 * retry.
 *
 * To keep memory bounded across a long SPA session with multiple purchases, the
 * cache is a small LRU: only the most-recently-used tokens are retained, and the
 * least-recently-used entry is evicted once the cap is exceeded. In-flight
 * deduplication is preserved for the retained tokens.
 */

const MASTER_CITIES = 'master_cities';

// Cap on retained purchase-token catalogs. A single SPA session realistically
// touches very few purchases at once; retaining a handful keeps dedup working
// while preventing unbounded growth of the full DIVIPOLA arrays.
const MAX_CACHED_TOKENS = 5;

// purchaseToken -> Promise<Array>. Map preserves insertion order, so the first
// key is the least-recently-used entry (touched entries are re-inserted last).
const requestCache = new Map();

/**
 * Fetches the master city catalog for the purchase's transporter, deduped per
 * purchase token so concurrent and subsequent callers reuse a single request.
 * @param {string} purchaseToken - The current purchase token.
 * @returns {Promise<Array>} Array of `{ id, name, city, state, department, slug }`.
 */
export const getMasterCities = (purchaseToken) => {
  if (requestCache.has(purchaseToken)) {
    // Touch: mark as most-recently-used so it survives the next eviction.
    const cached = requestCache.get(purchaseToken);
    requestCache.delete(purchaseToken);
    requestCache.set(purchaseToken, cached);
    return cached;
  }

  const request = apiRequest
    .get(MASTER_CITIES, {
      queryParams: { purchase_token: purchaseToken },
    })
    .catch((error) => {
      // Don't cache failures so a later mount can retry. Delete only this request's own
      // entry: an evicted request can reject long after a newer one for the same token was
      // cached, and deleting by token alone would drop that newer entry and silently switch
      // dedup off for every later caller.
      if (requestCache.get(purchaseToken) === request) {
        requestCache.delete(purchaseToken);
      }
      throw error;
    });

  requestCache.set(purchaseToken, request);

  // Evict the least-recently-used entry once the cap is exceeded.
  if (requestCache.size > MAX_CACHED_TOKENS) {
    const oldestToken = requestCache.keys().next().value;
    requestCache.delete(oldestToken);
  }

  return request;
};

/**
 * Clears the memoized master-cities requests (primarily for tests).
 */
export const clearMasterCitiesCache = () => requestCache.clear();

export default getMasterCities;
